import { Injectable, NgZone, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, Subscriber } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EventSourceService {
  private eventSource!: EventSource;

  constructor(
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  /**
   * Create an EventSource instance
   * @param url - SSE server API path
   * @param options - Configuration object for SSE (optional)
   */
  private getEventSource(url: string, options?: EventSourceInit): EventSource {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('EventSource is not available in the current platform.');
    }
    return new EventSource(url, options);
  }

  /**
   * Connect to server-sent events
   * @param url - SSE server API path
   * @param options - Configuration object for SSE (optional)
   * @param eventNames - List of event names to listen for (default includes 'message')
   */
  connectToServerSentEvents(url: string, options?: EventSourceInit, eventNames: string[] = []): Observable<Event> {
    if (!isPlatformBrowser(this.platformId)) {
      return new Observable((subscriber) => subscriber.error('SSE is not supported in the current platform.'));
    }

    this.eventSource = this.getEventSource(url, options);

    return new Observable((subscriber: Subscriber<Event>) => {
      this.eventSource.onerror = (error: any) => {
        this.zone.run(() => subscriber.error(error));
      };

      this.eventSource.onmessage = (event: MessageEvent) => {
        this.zone.run(() => subscriber.next(event));
      };

      eventNames.forEach((event: string) => {
        this.eventSource.addEventListener(event, (data: any) => {
          this.zone.run(() => subscriber.next(data));
        });
      });

      return () => {
        this.close();
        subscriber.complete();
      };
    });
  }

  /**
   * Close the connection
   */
  close(): void {
    if (this.eventSource) {
      this.eventSource.close();
    }
  }
}
